import { PlaceRef } from "@/types/entity"
import { FlightMapRoute } from "@/types/flights/flight-map-route"
import { Place, PlacePlaceholder, PlaceSummary } from "@/types/place"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { freeze } from "@/lib/utils/collections"
import { RootState } from "./redux-store"

export interface MapHoverSliceState {
  hoveredPlace: Place | PlacePlaceholder | undefined
  hoveredFlight: FlightMapRoute | undefined
  mapHoveredPlaceRef: PlaceRef | undefined
  hoveredItineraryEventId: number | undefined
}

export const mapHoverSliceInitialState = freeze<MapHoverSliceState>({
  hoveredPlace: undefined,
  hoveredFlight: undefined,
  mapHoveredPlaceRef: undefined,
  hoveredItineraryEventId: undefined,
})

const mapHoverSlice = createSlice({
  name: "mapHover",
  initialState: mapHoverSliceInitialState,
  reducers: {
    setHoveredPlace: (
      state,
      action: PayloadAction<Place | PlacePlaceholder | undefined>
    ) => {
      state.hoveredPlace = action.payload
      if (action.payload) {
        state.hoveredItineraryEventId = undefined
      }
    },
    clearHoveredPlaceIfMatches: (state, action: PayloadAction<PlaceRef>) => {
      if (state.hoveredPlace?.entity_ref === action.payload) {
        state.hoveredPlace = undefined
      }
    },
    setHoveredItineraryEventId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.hoveredItineraryEventId = action.payload
      if (action.payload != null) {
        state.hoveredPlace = undefined
        state.mapHoveredPlaceRef = undefined
      }
    },
    setHoveredFlight: (
      state,
      action: PayloadAction<FlightMapRoute | undefined>
    ) => {
      state.hoveredFlight = action.payload
    },
    setMapHoveredPlaceRef: (
      state,
      action: PayloadAction<PlaceRef | undefined>
    ) => {
      state.mapHoveredPlaceRef = action.payload
    },
    /** Replace the hovered place when its entity_ref matches an updated place. */
    updatePlaceIfHighlighted: (state, action: PayloadAction<Place>) => {
      const newPlace = action.payload
      if (state.hoveredPlace?.entity_ref === newPlace.entity_ref) {
        state.hoveredPlace = newPlace
      }
    },
  },
})

const selectSliceState = (state: RootState) => state.mapHoverSlice

export const selectHoveredPlace = createSelector(
  [selectSliceState],
  (state) => state.hoveredPlace
)

/**
 * The hovered place's ref alone. `hoveredPlace` holds a whole `Place`, so its
 * identity changes on every hover; anything that only needs to know *which*
 * place is hovered subscribes here instead and stops re-rendering.
 */
export const selectHoveredPlaceRef = (state: RootState) =>
  selectSliceState(state).hoveredPlace?.entity_ref

export const selectHoveredFlight = createSelector(
  [selectSliceState],
  (state) => state.hoveredFlight
)

export const selectMapHoveredPlaceRef = createSelector(
  [selectSliceState],
  (state) => state.mapHoveredPlaceRef
)

export const selectHoveredItineraryEventId = createSelector(
  [selectSliceState],
  (state) => state.hoveredItineraryEventId
)

export const selectIsItineraryEventHoveredOnMap = createSelector(
  [
    selectHoveredItineraryEventId,
    (_state: RootState, eventId: number) => eventId,
  ],
  (hoveredItineraryEventId, eventId) => hoveredItineraryEventId === eventId
)

/**
 * Hovered off the map (a list card, an itinerary row): highlight it on the map.
 * A plain function, not a `createSelector`: every marker calls this on every
 * dispatch, and memoizing one string compare costs more — in per-call
 * allocation, and in a cache entry per (hovered ref, marker ref) pair that is
 * never evicted — than it saves.
 */
export const selectIsPlaceHoveredInList = (
  state: RootState,
  entityRef: PlaceRef | undefined
) => entityRef != null && selectHoveredPlaceRef(state) === entityRef

/** Hovered on the map: highlight it in the list. */
export const selectIsPlaceHoveredOnMap = createSelector(
  [
    selectMapHoveredPlaceRef,
    (_state: RootState, place: Place | PlacePlaceholder | PlaceSummary) =>
      place,
  ],
  (mapHoveredPlaceRef, place) => mapHoveredPlaceRef === place.entity_ref
)

export const {
  setHoveredPlace,
  clearHoveredPlaceIfMatches,
  setHoveredItineraryEventId,
  setHoveredFlight,
  setMapHoveredPlaceRef,
  updatePlaceIfHighlighted,
} = mapHoverSlice.actions

export type MapHoverSlice = typeof mapHoverSlice
export default mapHoverSlice
